import { OptionalResourceParams } from './ResourceParams';
import { ResourceObject } from './document/ResourceObject';
import { CreateOrUpdateObject } from './document/CreateOrUpdateObject';
import { ResourceIdentifierObject } from './document/ResourceIdentifierObject';
import { JsonDocumentInterface } from './document';
import { Typed, TypedAttributes } from './TypedAttributes';

export class JsonApiService {
    public makeUrl(url: string, resourceParams?: OptionalResourceParams): string {
        if (resourceParams) {
            let filterString = '';

            Object.keys(resourceParams).forEach((key: string) => {
                if (Array.isArray(resourceParams[key]) && resourceParams[key].length) {
                    filterString += `&${key}=${resourceParams[key].filter(Boolean).join()}`;
                } else if (typeof resourceParams[key] === 'object') {
                    Object
                        .keys(resourceParams[key])
                        .forEach((filterKey) => {
                            if (resourceParams[key][filterKey]) {
                                // eslint-disable-next-line max-len
                                filterString += `&${key}[${filterKey}]=${resourceParams[key][filterKey]}`;
                            }
                        });
                } else if (resourceParams[key]) {
                    filterString += `&${key}=${resourceParams[key]}`;
                }
            });

            return `${url}?${filterString.substr(1)}`;
        }

        return url;
    }

    public makeUpdateObject<T>(
        type: string,
        resource: ResourceObject<TypedAttributes<T>>,
        data: T & { [key: string]: any }
    ): CreateOrUpdateObject<T> {
        const updatedValues: Partial<T> = {};

        Object.keys(resource.attributes).forEach((attribute: string) => {
            if (data[attribute]
                && (resource.attributes[attribute] !== data[attribute]
                    || (resource.attributes[attribute] as Typed).value !== data[attribute]
                )
            ) {
                updatedValues[attribute as keyof T] = data[attribute];
            }
        });

        return {
            data: {
                type,
                id: resource.id,
                attributes: updatedValues
            }
        };
    }

    public getRelationsByTypeForResource(
        resource: ResourceObject<any>,
        type: Array<string>,
    ): ResourceIdentifierObject | Array<ResourceIdentifierObject> | null {
        if (!resource.relationships) {
            return null;
        }

        let relations = null;

        Object.keys(resource.relationships).forEach((relationKey: string) => {
            // handle not in included
            if (!resource.relationships![relationKey].data) {
                return;
            }

            // Handle one-to-many relation
            if (Array.isArray(resource.relationships![relationKey].data)) {
                relations = (resource.relationships![relationKey].data as Array<ResourceIdentifierObject>)
                    .filter((relation: ResourceIdentifierObject) => type.includes(relation.type));
                return;
            }

            // handle one-to-one
            if (type.includes((resource.relationships![relationKey].data as ResourceIdentifierObject).type)) {
                relations = resource.relationships![relationKey].data;
            }
        });

        return relations;
    }

    public getIncludesArrayByType<T>(
        resource: ResourceObject<any>,
        type: Array<string>,
        included?: Array<ResourceObject<T>>,
    ): Array<ResourceObject<T> | null> | null {
        if (!included) {
            return null;
        }

        let relations = this.getRelationsByTypeForResource(resource, type);

        if (!relations) {
            return null;
        }

        if (!Array.isArray(relations)) {
            relations = [relations];
        }

        return relations.map((relation) => included.find(
            (include: ResourceObject<T>) => type.includes(include.type)
                && include.id === (relation as ResourceIdentifierObject).id
        ) || null);
    }

    public getIncludeByType<T>(
        resource: ResourceObject<any>,
        type: Array<string>,
        included?: Array<ResourceObject<T>>,
        // @ts-ignore
    ): ResourceObject<T> | null {
        if (!included) {
            return null;
        }

        let relations = this.getRelationsByTypeForResource(resource, type);

        if (!relations) {
            return null;
        }

        // @TODO one-to-many relations not supported yet. Fetching always the first
        if (Array.isArray(relations)) {
            [relations] = relations;
        }

        return included.find(
            (include: ResourceObject<T>) => type.includes(include.type)
                && include.id === (relations as ResourceIdentifierObject).id
        ) || null;
    }

    public getIncludedByRelation<T>(
        data: ResourceObject<any>,
        relations: Array<string>,
        included?: Array<ResourceObject<any>>
    ): Array<ResourceObject<T>> | null {
        if (!included || !data.relationships) {
            return null;
        }

        const relationsToInclude: Array<ResourceIdentifierObject> = [];

        Object.keys(data.relationships).forEach((relation: string) => {
            if (relations.includes(relation) && data.relationships![relation].data) {
                if (Array.isArray(data.relationships![relation].data)) {
                    (data.relationships![relation].data as Array<ResourceIdentifierObject>)
                        .forEach((relData: ResourceIdentifierObject) => {
                            relationsToInclude.push(relData);
                        });
                } else {
                    relationsToInclude.push(data.relationships![relation].data as ResourceIdentifierObject);
                }
            }
        });

        const includesToReturn: Array<ResourceObject<any>> = [];

        if (relationsToInclude.length) {
            // @ts-ignore
            relationsToInclude.forEach((relationToInclude: ResourceIdentifierObject) => {
                included.forEach((include: ResourceObject<any>) => {
                    if (include.type === relationToInclude.type && include.id === relationToInclude.id) {
                        includesToReturn.push(include);
                    }
                });
            });

            return includesToReturn;
        }

        return null;
    }

    public isEmpty(
        doc: JsonDocumentInterface<any>
    ): boolean {
        if (doc.data === null) {
            return true;
        }

        if (Array.isArray(doc.data)) {
            return !doc.data.length;
        }

        return false;
    }

    public toArray<T = any>(doc: JsonDocumentInterface<T>): Array<JsonDocumentInterface<T>> {
        const arr: Array<JsonDocumentInterface<T>> = [];
        if (this.isEmpty(doc)) {
            return arr;
        }

        if (!Array.isArray(doc.data)) {
            arr.push(doc);

            return arr;
        }

        doc.data.forEach((resource) => {
            const docClone = { ...doc, data: resource };
            arr.push(docClone);
        });

        return arr;
    }

    public toResourceArray<T = any>(doc: JsonDocumentInterface<T>): Array<ResourceObject<T>> {
        if (this.isEmpty(doc)) {
            return [];
        }

        if (Array.isArray(doc.data)) {
            return doc.data;
        }

        return [doc.data as ResourceObject<T>];
    }
}
